import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import './ProposalDetails.css';

function dedupeDocs(list) {
  const seen = new Set();
  const out = [];
  for (const d of Array.isArray(list) ? list : []) {
    const id = String(d.document_id || '');
    if (!seen.has(id)) {
      seen.add(id);
      out.push(d);
    }
  }
  return out;
}

const ProposalDetails = () => {
  const { proposalId } = useParams();
  const navigate = useNavigate();

  const [proposal, setProposal] = useState(null);
  const [docs, setDocs] = useState([]);

  const [childrenList, setChildrenList] = useState([]);
  const [childrenError, setChildrenError] = useState('');
  const [newTitle, setNewTitle] = useState('');
  const [openDocPath, setOpenDocPath] = useState(null);

  const [meetings, setMeetings] = useState([]);
  const [statusHistory, setStatusHistory] = useState([]);

  const [secMsg, setSecMsg] = useState('');
  const [includeReviewerComments, setIncludeReviewerComments] = useState(true);
  const [isSending, setIsSending] = useState(false);
  const [sendError, setSendError] = useState('');
  const [sendOk, setSendOk] = useState('');

  const roleId = parseInt(localStorage.getItem('role_id'), 10);
  const isResearcher = roleId === 1;
  const isSecretary  = roleId === 4;

  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3000/api';
  const API_ORIGIN = API_BASE_URL.replace(/\/api\/?$/, '');

  useEffect(() => {
    let alive = true;
    const fetchProposal = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/proposals/details/${proposalId}`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        });
        if (!response.ok) throw new Error('Failed to fetch proposal details');
        const data = await response.json();
        if (!alive) return;

        setProposal(data);
        setNewTitle(data?.proposal?.title || '');
        setDocs(dedupeDocs(data?.documents || []));
      } catch (err) {
        console.error(err);
      }
    };
    fetchProposal();
    return () => { alive = false; };
  }, [API_BASE_URL, proposalId]);

  useEffect(() => {
    let alive = true;
    const fetchChildren = async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/proposals/${proposalId}/children`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        });
        if (!alive) return;
        if (res.ok) {
          const data = await res.json();
          setChildrenList(data.children || []);
        } else if (res.status !== 404) {
          setChildrenError('Failed to load requests');
        }
      } catch {
        setChildrenError('');
      }
    };
    fetchChildren();
    return () => { alive = false; };
  }, [API_BASE_URL, proposalId]);

  useEffect(() => {
    let alive = true;
    const fetchMeetings = async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/proposals/${proposalId}/meetings`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        });
        if (!alive) return;
        if (res.ok) {
          const data = await res.json();
          setMeetings(Array.isArray(data) ? data : []);
        }
      } catch (e) {
        console.error('Failed to load meetings', e);
      }
    };
    fetchMeetings();
    return () => { alive = false; };
  }, [API_BASE_URL, proposalId]);

  useEffect(() => {
    let alive = true;
    const fetchHistory = async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/proposals/${proposalId}/status-history`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        });
        if (!alive) return;
        if (res.ok) {
          const data = await res.json();
          setStatusHistory(Array.isArray(data) ? data : []);
        }
      } catch (e) {
        console.error('Failed to load status history', e);
      }
    };
    fetchHistory();
    return () => { alive = false; };
  }, [API_BASE_URL, proposalId]);

  const withToken = (url) => {
    const token = localStorage.getItem('token');
    if (!token || !url.includes('/api/files/')) return url;
    const separator = url.includes('?') ? '&' : '?';
    return `${url}${separator}token=${encodeURIComponent(token)}`;
  };

  const openModal = (docPath) => setOpenDocPath(withToken(`${API_ORIGIN}${docPath}`));
  const closeModal = () => setOpenDocPath(null);

  const goNewRequest = (kind) => navigate(`/proposal/${proposalId}/request?kind=${kind}`);

  const isLoading = !proposal;
  const currentStatus = proposal?.proposal?.status || '';
  const isApproved = currentStatus === 'Approved';
  const isCompletedStudy = currentStatus === 'Completed Study';
  const isRejected = currentStatus === 'Rejected';

  const showSecretaryChangePanel = isSecretary && (
    currentStatus === 'Under Review' ||
    currentStatus === 'Request Resubmit' ||
    currentStatus === 'Resubmitted'
  );

  const handleSecretarySend = async () => {
    setIsSending(true);
    setSendError('');
    setSendOk('');
    try {
      const res = await fetch(
        `${API_BASE_URL}/reviewer-review/${proposalId}/secretary-send-resubmit-email`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
          body: JSON.stringify({
            message: secMsg,
            includeReviewerComments,
          }),
        }
      );
      const data = await res.json();
      if (!res.ok) throw new Error(data?.error || 'Failed to send email');
      setSendOk(data?.message || 'Email sent to researcher.');

      try {
        const response = await fetch(`${API_BASE_URL}/proposals/details/${proposalId}`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        });
        if (response.ok) {
          const d = await response.json();
          setProposal(d);
          setDocs(dedupeDocs(d?.documents || []));
        }
      } catch {}
    } catch (e) {
      setSendError(e.message || 'Failed to send email.');
    } finally {
      setIsSending(false);
    }
  };

  return (
    <div className="proposal-details-container">
      {isLoading ? (
        <div>Loading...</div>
      ) : (
        <>
          <section className="submitted-details">
            <div className="details-header">
              <h2>Submission Details</h2>
              <div className="status-pill">{currentStatus}</div>
            </div>

            <div className="details-grid">
              <div>
                <span className="muted">Title</span>
                <div className="value">{newTitle}</div>
              </div>
              <div>
                <span className="muted">Submission Date</span>
                <div className="value">
                  {proposal.proposal.submission_date
                    ? new Date(proposal.proposal.submission_date).toLocaleDateString()
                    : '—'}
                </div>
              </div>
              <div>
                <span className="muted">Proposal ID</span>
                <div className="value">{proposal.proposal.custom_id || proposal.proposal.proposal_id}</div>
              </div>
            </div>
          </section>

          {showSecretaryChangePanel && (
            <section className="card">
              <h3 className="section-title">Secretary Action — Send Change Request to Researcher</h3>
              <p className="section-subtitle">
                This will email the researcher. The list of documents flagged for resubmission will be included automatically.
              </p>

              <div className="details-grid">
                <div style={{ gridColumn: '1 / -1' }}>
                  <label className="muted" htmlFor="secMsg">Message to researcher (optional)</label>
                  <textarea
                    id="secMsg"
                    className="input-std"
                    rows={4}
                    placeholder="Add an overall message (optional)…"
                    value={secMsg}
                    onChange={(e) => setSecMsg(e.target.value)}
                  />
                </div>

                <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <input
                    type="checkbox"
                    checked={includeReviewerComments}
                    onChange={(e) => setIncludeReviewerComments(e.target.checked)}
                  />
                  Include reviewer comments in email
                </label>

                <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 8 }}>
                  <button
                    className="btn primary"
                    disabled={isSending}
                    onClick={handleSecretarySend}
                  >
                    {isSending ? 'Sending…' : 'Send change request to researcher'}
                  </button>
                </div>

                {sendOk && <div className="document-status accepted" style={{ marginTop: 8 }}>{sendOk}</div>}
                {sendError && <div className="error-text" style={{ marginTop: 8 }}>{sendError}</div>}
              </div>
            </section>
          )}

          {isResearcher && (
            <section className="card">
              <h3 className="section-title">Actions</h3>
              <p className="section-subtitle">
                These actions are available when the proposal is <b>Approved</b>.
              </p>
              <div className="actions-grid">
                <button
                  className="btn primary"
                  disabled={isCompletedStudy || isRejected}
                  onClick={() => goNewRequest('EXTENSION')}
                  aria-label="Request Extension"
                >
                  Request Extension
                </button>
                <button
                  className="btn primary"
                  disabled={isCompletedStudy || isRejected}
                  onClick={() => goNewRequest('AMENDMENT')}
                  aria-label="Request Amendment"
                >
                  Request Amendment
                </button>
                <button
                  className="btn primary"
                  disabled={!isApproved || isCompletedStudy}
                  onClick={() => goNewRequest('PROGRESS')}
                  aria-label="Submit Progress Report"
                >
                  Submit Progress Report
                </button>
                <button
                  className="btn primary"
                  disabled={!isApproved || isCompletedStudy}
                  onClick={() => goNewRequest('FINAL')}
                  aria-label="Submit Final Report"
                >
                  Submit Final Report
                </button>
              </div>
            </section>
          )}

          <section className="documents-list card">
            <h3 className="section-title">Submitted Documents</h3>
            <p className="section-subtitle">Click on the title to view the document</p>

            <div className="document-grid">
              {docs.map((doc) => (
                <div
                  className="document-tile clickable-tile"
                  key={doc.document_id}
                  onClick={() => openModal(doc.path)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => e.key === 'Enter' && openModal(doc.path)}
                >
                  <span className="document-name">{doc.document_name}</span>
                  <span
                    className={`document-status ${
                      doc.status ? doc.status.toLowerCase().replace(/\s+/g, '-') : 'none'
                    }`}
                    title={doc.comment || ''}
                  >
                    {doc.status || '—'}
                  </span>
                </div>
              ))}
            </div>
          </section>

          {proposal.generalComments?.length > 0 && (
            <section className="card">
              <h3 className="section-title">General Comments from Reviewers</h3>
              <ul className="comments-list">
                {proposal.generalComments.map((gc, idx) => (
                  <li key={idx} className="comment-row">
                    <div className="comment-meta">
                      <span>{gc.first_name} {gc.last_name}</span>
                      <span> • {gc.reviewed_at ? new Date(gc.reviewed_at).toLocaleString() : ''}</span>
                    </div>
                    <div className="comment-text">{gc.overall_comment || '—'}</div>
                  </li>
                ))}
              </ul>
            </section>
          )}

          {meetings.length > 0 && (
            <section className="card">
              <h3 className="section-title">Meeting Timeline</h3>
              <ul className="comments-list">
                {meetings.map((m) => {
                  const reqs = Array.isArray(m.required_resubmissions) ? m.required_resubmissions : [];
                  const items = reqs.filter(r => r?.action === 'resubmit');
                  return (
                    <li key={m.id} className="comment-row">
                      <div className="comment-meta">
                        {m.meeting_date ? new Date(m.meeting_date).toLocaleString() : '—'} • {m.decision || '—'}
                      </div>
                      <div className="comment-text">
                        {m.note || '—'}
                        {!!items.length && (
                          <div style={{ marginTop: 8 }}>
                            <b>Required resubmissions:</b>
                            <ul style={{ marginTop: 4, paddingLeft: 18 }}>
                              {items.map((r, i) => (
                                <li key={`${m.id}-${i}`}>
                                  <b>{r.document_name || r.document_id}</b>
                                  {r.comment ? ` — ${r.comment}` : ''}
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>
                    </li>
                  );
                })}
              </ul>
            </section>
          )}

          {statusHistory.length > 0 && (
            <section className="card">
              <h3 className="section-title">Status History</h3>
              <ul className="comments-list">
                {statusHistory.map((h) => (
                  <li key={h.id} className="comment-row">
                    <div className="comment-meta">
                      {h.changed_at ? new Date(h.changed_at).toLocaleString() : '—'} •&nbsp;
                      {h.old_status || '—'} → <b>{h.new_status}</b>
                      {h.changed_by_name ? ` • by ${h.changed_by_name}` : ''}
                    </div>
                    <div className="comment-text">{h.note || '—'}</div>
                  </li>
                ))}
              </ul>
            </section>
          )}

          {childrenList.length > 0 && (
            <section className="card">
              <h3 className="section-title">Extension &amp; Amendment Requests</h3>
              <div className="child-grid">
                {childrenList.map((c) => (
                  <div key={c.child_id} className="child-card">
                    <div className="child-top">
                      <span className="child-kind">{c.kind}</span>
                      <span className={`child-status ${c.status.toLowerCase().replace(/\s+/g, '-')}`}>
                        {c.status}
                      </span>
                    </div>
                    <div className="child-meta">
                      {c.submitted_at && (
                        <span className="muted">
                          Submitted {new Date(c.submitted_at).toLocaleDateString()}
                        </span>
                      )}
                    </div>
                    <button
                      className="btn ghost child-view-btn"
                      onClick={() => navigate(`/children/${c.child_id}`)}
                      aria-label={`View ${c.kind}`}
                    >
                      View Details
                    </button>
                  </div>
                ))}
              </div>
              {childrenError && <div className="error-text">{childrenError}</div>}
            </section>
          )}

          {openDocPath && (
            <div className="modal">
              <div className="modal-content">
                <button className="close-modal" onClick={closeModal} aria-label="Close preview">&times;</button>
                <iframe
                  src={openDocPath}
                  width="100%"
                  height="600px"
                  title="Document Viewer"
                  style={{ border: 'none' }}
                />
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default ProposalDetails;
